import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { type AuthUser } from 'wasp/auth';
import { ArrowLeft, User as UserIcon, Mail, Shield, Save, X, MapPin, Award, TrendingUp, Info } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { useQuery, getUserById, getAllLocations, updateUserPrimaryLocation, updateLocationSubscription, createLocationMembershipForUser, deleteLocationMembershipForUser } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { toast } from '../../../hooks/use-toast';
import { 
  IndividualEditPageLayout, 
  SidebarSection, 
  ActionButtons,
  ActionButton,
  ActionButtonSecondary
} from '../../../components/layout';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';

interface UserEditPageProps {
  user: AuthUser;
}

const UserEditPage = ({ user: _user }: UserEditPageProps) => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { data: currentUser } = useAuth();
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    primaryLocationId: null as string | null,
  });
  
  const [subscriptions, setSubscriptions] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [primaryLocationSearch, setPrimaryLocationSearch] = useState('');
  const [subscriptionSearch, setSubscriptionSearch] = useState('');
  const [savedMembershipIds, setSavedMembershipIds] = useState<string[]>([]);

  // Fetch user details
  const { data: userData, isLoading: fetchingUser } = useQuery(getUserById, {
    userId: userId || '',
  });

  // Fetch all locations for admin selection
  const locationsQuery = useQuery(getAllLocations, {
    page: 1,
    limit: 100,
  });
  const allLocationsData = locationsQuery.data as { locations: any[]; total: number; page: number; totalPages: number } | undefined;

  // Update form data when user data is fetched
  useEffect(() => {
    if (userData) {
      setFormData({
        email: userData.email || '',
        username: userData.username || '',
        primaryLocationId: userData.primaryLocationId || null,
      });
      
      // Initialize subscriptions from existing memberships
      const subs: Record<string, boolean> = {};
      userData.locationMemberships?.forEach((membership: any) => {
        subs[membership.locationId] = membership.isSubscribed;
      });
      setSubscriptions(subs);

      // Track currently saved membership ids for controlled rendering
      setSavedMembershipIds((userData.locationMemberships || []).map((m: any) => m.locationId));
    }
  }, [userData, allLocationsData]);

  // Track changes
  useEffect(() => {
    if (userData) {
      const hasFormChanges = 
        formData.email !== (userData.email || '') ||
        formData.username !== (userData.username || '') ||
        formData.primaryLocationId !== (userData.primaryLocationId || null);
      
      const membershipLocationIds = new Set(userData.locationMemberships?.map((m: any) => m.locationId) || []);
      const hasSubscriptionChanges = 
        userData.locationMemberships?.some((membership: any) =>
          subscriptions[membership.locationId] !== membership.isSubscribed
        ) || 
        Object.entries(subscriptions).some(([locId, isSubbed]) => {
          const wasMember = membershipLocationIds.has(locId);
          return isSubbed && !wasMember; // Subscribing to new location
        });
      
      setHasChanges(hasFormChanges || !!hasSubscriptionChanges);
    }
  }, [formData, subscriptions, userData]);

  const handleBack = () => {
    navigate(`/admin/users/${userId}`);
  };

  const handleInputChange = (field: string, value: string | boolean | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Auto-subscribe to primary location when selected
    if (field === 'primaryLocationId' && value) {
      setSubscriptions(prev => ({
        ...prev,
        [value as string]: true
      }));
    }
  };

  const handleSubscriptionChange = (locationId: string, isSubscribed: boolean) => {
    setSubscriptions(prev => ({
      ...prev,
      [locationId]: isSubscribed
    }));
  };

  const handleSave = async () => {
    if (!userData) return;
    
    setIsLoading(true);
    try {
      // Get existing membership location IDs
      const membershipLocationIds = new Set(userData.locationMemberships?.map((m: any) => m.locationId) || []);

      // Handle membership deletions (locations toggled off)
      for (const membership of (userData.locationMemberships || [])) {
        const newSubscriptionStatus = subscriptions[membership.locationId];
        if (newSubscriptionStatus === false) {
          // Delete membership when toggled off
          await deleteLocationMembershipForUser({
            userId: userData.id.toString(),
            locationId: membership.locationId
          });
        }
      }

      // Handle new subscriptions (locations the user wasn't subscribed to before)
      for (const [locationId, isSubscribed] of Object.entries(subscriptions)) {
        if (isSubscribed && !membershipLocationIds.has(locationId)) {
          await createLocationMembershipForUser({
            userId: userData.id.toString(),
            locationId: locationId,
          });
        }
      }

      // Update primary location if changed (ensure membership exists via validation in backend)
      if (formData.primaryLocationId !== (userData.primaryLocationId || null)) {
        await updateUserPrimaryLocation({
          userId: userData.id.toString(),
          primaryLocationId: formData.primaryLocationId
        });
      }

      // Update locally saved membership ids to reflect persisted state
      {
        const existingSet = new Set<string>((userData.locationMemberships || []).map((m: any) => m.locationId));
        // Remove those toggled off from existing
        for (const m of (userData.locationMemberships || [])) {
          const newStatus = subscriptions[m.locationId];
          if (newStatus === false) {
            existingSet.delete(m.locationId);
          }
        }
        // Add newly selected ones
        for (const [locId, isSubbed] of Object.entries(subscriptions)) {
          if (isSubbed) existingSet.add(locId);
        }
        setSavedMembershipIds(Array.from(existingSet));
      }

      toast({
        title: "User updated successfully",
        description: "User information has been saved.",
      });
      
      setHasChanges(false);
      navigate('/admin/users');
    } catch (error) {
      console.error('Error updating user:', error);
      toast({
        title: "Error updating user",
        description: error instanceof Error ? error.message : "There was an error saving the user information.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    handleBack();
  };

  if (fetchingUser) {
    return (
      <IndividualEditPageLayout layoutVariant="taxonomy" authUser={_user}>
        <div className="flex justify-center items-center py-12">
          <div className="text-center">Loading...</div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (!userData) {
    return (
      <IndividualEditPageLayout layoutVariant="taxonomy" authUser={_user}>
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">User Not Found</h2>
            <p className="text-gray-600 mb-4">The user you're looking for doesn't exist.</p>
          </div>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const isCurrentUser = currentUser?.id === userData.id;

  return (
    <IndividualEditPageLayout
      layoutVariant="taxonomy"
      authUser={_user}
      sidebarContent={
        <>
          <SidebarSection isFirst={true}>
            <ActionButtons>
              <ActionButton
                onClick={handleSave}
                disabled={!hasChanges || isLoading}
                icon={Save}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </ActionButton>
              <ActionButtonSecondary
                onClick={handleCancel}
              >
                Back
              </ActionButtonSecondary>
            </ActionButtons>
          </SidebarSection>

          <SidebarSection title="User Stats">
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Points Earned
                </span>
                <span className="font-medium">{userData.pointsEarned || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Approval Score
                </span>
                <span className="font-medium">{userData.approvalScore || 0}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Posts Approved</span>
                <span className="font-medium text-green-600">{userData.totalPostsApproved || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Posts Rejected</span>
                <span className="font-medium text-red-600">{userData.totalPostsRejected || 0}</span>
              </div>
            </div>
          </SidebarSection>

          <SidebarSection title="Quick Info">
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-muted-foreground">User ID:</span>
                <p className="font-mono text-xs break-all">{userData.id}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Created:</span>
                <p className="font-medium">{userData.createdAt ? new Date(userData.createdAt).toLocaleDateString() : 'N/A'}</p>
              </div>
            </div>
          </SidebarSection>
        </>
      }
    >
      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
              <UserIcon className="w-6 h-6 text-gray-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Edit User</h1>
              <p className="text-sm text-gray-600">{userData.email}</p>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email Address
              </Label>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="flex-1"
                  placeholder="Enter email address"
                  disabled
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium text-gray-700">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                placeholder="Enter username"
                disabled
              />
            </div>
          </div>
        </div>

        {/* Location Settings */}
        <div className="border-t pt-6 space-y-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Location Settings
          </h3>
          
          <div className="space-y-4">
            {/* Primary Location with Search */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium text-gray-700">
                  Primary Location
                </Label>
                <div 
                  className="relative group cursor-help"
                  title="User can always post in their primary location without approval (once they have a good track record)"
                >
                  <Info className="w-4 h-4 text-gray-400" />
                  <div className="hidden group-hover:block absolute bottom-full left-0 mb-2 w-48 bg-gray-900 text-white text-xs rounded p-2 z-10 whitespace-normal">
                    User can always post in their primary location without approval (once they have a good track record)
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder="Search and select primary location..."
                  value={primaryLocationSearch}
                  onChange={(e) => setPrimaryLocationSearch(e.target.value)}
                  className="w-full"
                />
                
                {formData.primaryLocationId && (
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="font-medium text-sm">
                      {allLocationsData?.locations?.find((l: any) => l.id === formData.primaryLocationId)?.name || formData.primaryLocationId}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleInputChange('primaryLocationId', null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                
                {primaryLocationSearch && (
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {(allLocationsData?.locations || [])
                      .filter((loc: any) =>
                        loc.name.toLowerCase().includes(primaryLocationSearch.toLowerCase())
                      )
                      .map((location: any) => (
                        <button
                          key={location.id}
                          onClick={() => {
                            handleInputChange('primaryLocationId', location.id);
                            setPrimaryLocationSearch('');
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 border-b last:border-b-0 text-sm"
                        >
                          {location.name}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>

            {/* Location Memberships */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-700">
                Subscribed Locations
              </Label>
              
              {/* Current Memberships (excluding primary) */}
              {(() => {
                const allLocs = new Map<string, { id: string; name: string }>();
                (allLocationsData?.locations || []).forEach((l: any) => {
                  allLocs.set(l.id, { id: l.id, name: l.name });
                });

                const existingIdsSet = new Set<string>(savedMembershipIds);
                // Compose display IDs: existing (saved) + newly added pending (subscriptions true but not saved yet)
                const pendingAddedIds = Object.entries(subscriptions)
                  .filter(([id, v]) => !!v && !existingIdsSet.has(id))
                  .map(([id]) => id);
                const combinedIds = Array.from(new Set<string>([...existingIdsSet, ...pendingAddedIds]));
                // Exclude primary from display
                const filteredIds = combinedIds.filter((id) => id !== formData.primaryLocationId);

                const display = filteredIds.map((id) => {
                  const fromSaved = (userData.locationMemberships || []).find((m: any) => m.locationId === id);
                  const name = fromSaved?.location?.name || allLocs.get(id)?.name || id;
                  return { id, name };
                });

                return display.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-600 font-medium">Member Locations</p>
                      <p className="text-xs text-gray-500">Primary location shown separately above.</p>
                    </div>
                    {display.map((loc) => {
                      const isActive = subscriptions[loc.id] ?? false;
                      return (
                        <div key={loc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{loc.name}</p>
                            <p className="text-xs text-gray-500">
                              {isActive ? 'Member' : 'Will be removed on Save'}
                            </p>
                          </div>
                          <Switch
                            checked={isActive}
                            onCheckedChange={(value) => handleSubscriptionChange(loc.id, value)}
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : null;
              })()}

              {/* Search to Add New Locations */}
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder="Search and add location..."
                  value={subscriptionSearch}
                  onChange={(e) => setSubscriptionSearch(e.target.value)}
                  className="w-full"
                />
                
                {subscriptionSearch && (
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {(allLocationsData?.locations || [])
                      .filter((loc: any) => {
                        const hasExistingMembership = userData.locationMemberships?.some((m: any) => m.locationId === loc.id);
                        return !hasExistingMembership && loc.name.toLowerCase().includes(subscriptionSearch.toLowerCase());
                      })
                      .map((location: any) => (
                        <button
                          key={location.id}
                          onClick={() => {
                            handleSubscriptionChange(location.id, true);
                            setSubscriptionSearch('');
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 border-b last:border-b-0 text-sm"
                        >
                          {location.name}
                        </button>
                      ))}
                  </div>
                )}
              </div>

              {!userData.locationMemberships || userData.locationMemberships.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No location memberships yet. Search and add locations above.</p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </IndividualEditPageLayout>
  );
};

export default UserEditPage;



